import { ErrorBoundary as BaseErrorBoundary } from 'react-error-boundary';

import { useTranslate } from '@growtherapy/i18n';

import { IntentionNotSetBasicModal, IntentionNotSetModal } from '@grow-therapy-team/seedling-components';

import { useCreateSessionIntentionMutation } from '../../hooks/intentions/useCreateSessionIntentionMutation';
import { useAppDependenciesContext } from '../../state';
import { useSendSessionIntentionSetCallback } from '../messages/useSendSessionIntentionSetCallback';

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return <BaseErrorBoundary FallbackComponent={() => null}>{children}</BaseErrorBoundary>;
}

export const IntentionNotSetModalWrapper = ({
  isOpen,
  onClose,
  clientName,
  providerImage,
  onSendPrompt,
  clientUserShortId,
  appointmentShortId,
  providerCanSetIntentions,
}: {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  providerImage: string;
  onSendPrompt: () => void;
  clientUserShortId: string;
  appointmentShortId: string;
  providerCanSetIntentions: boolean;
}) => {
  const [createSessionIntention] = useCreateSessionIntentionMutation();
  const sendSessionIntentionSet = useSendSessionIntentionSetCallback();

  const { logger, toast } = useAppDependenciesContext();
  const t = useTranslate();
  const handleSetIntention = async (intention: string) => {
    try {
      const result = await createSessionIntention({
        variables: {
          clientUserShortId,
          appointmentShortId,
          contentText: intention,
        },
      });

      const shortId = result.data?.createSessionIntention?.sessionIntention?.shortId;
      if (shortId) {
        await sendSessionIntentionSet(shortId);
        onClose();
      }
    } catch (error) {
      logger.error('Error setting session intention', { error });
      toast.error(t('telehealth-core:sessionIntention.couldNotSetIntention'));
    }
  };

  return (
    <ErrorBoundary>
      {providerCanSetIntentions ? (
        <IntentionNotSetModal
          name={clientName}
          isOpen={isOpen}
          onClose={onClose}
          onSendPrompt={onSendPrompt}
          onSetIntention={handleSetIntention}
        />
      ) : (
        <IntentionNotSetBasicModal
          name={clientName}
          isOpen={isOpen}
          onClose={onClose}
          providerPhotoUrl={providerImage}
          onSendPrompt={onSendPrompt}
        />
      )}
    </ErrorBoundary>
  );
};
